/**
 * Claw 3D View — GLB model + status-driven animation
 * Three.js + React Three Fiber + drei
 *
 * Supports GLB models with status-driven animations and particle effects.
 * Falls back to CSS version when WebGL is unavailable.
 */

import { Component, useRef, useMemo, Suspense, type ReactNode } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Float, useGLTF, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

const MODEL_PATH = './models/Meshy_AI_A_Scout_from_an_alien_0402022732_texture.glb';

/* --- WebGL detection --- */
function isWebGLAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch {
    return false;
  }
}

/* --- Error Boundary --- */
interface ErrorBoundaryProps { fallback: ReactNode; children: ReactNode }
interface ErrorBoundaryState { hasError: boolean }

class WebGLErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/* --- Status Config --- */
const STATUS_CONFIG: Record<string, {
  bgTop: string;
  bgBottom: string;
  label: string;
  intensity: number;
  dotColor: string;
  glowColor: string;
  floatSpeed: number;
  floatIntensity: number;
}> = {
  online: {
    bgTop: '#FFF9F0', bgBottom: '#FFE8CC',
    label: '\u5728\u7EBF', intensity: 1, dotColor: '#00B894',
    glowColor: '#00ff88', floatSpeed: 1.5, floatIntensity: 0.5,
  },
  working: {
    bgTop: '#FFF5E6', bgBottom: '#FFD9A0',
    label: '\u5DE5\u4F5C\u4E2D', intensity: 1.4, dotColor: '#FF9F43',
    glowColor: '#ffaa00', floatSpeed: 2.5, floatIntensity: 0.8,
  },
  thinking: {
    bgTop: '#F5F0FF', bgBottom: '#E0D4FF',
    label: '\u601D\u8003\u4E2D', intensity: 0.8, dotColor: '#a78bfa',
    glowColor: '#a78bfa', floatSpeed: 1, floatIntensity: 0.4,
  },
  busy: {
    bgTop: '#FFF0EC', bgBottom: '#FFD5C8',
    label: '\u5FD9\u788C', intensity: 1.3, dotColor: '#E17055',
    glowColor: '#ff6600', floatSpeed: 3, floatIntensity: 0.6,
  },
  idle: {
    bgTop: '#FFFBF0', bgBottom: '#FFF0C8',
    label: '\u4F11\u606F\u4E2D', intensity: 0.6, dotColor: '#FDCB6E',
    glowColor: '#ffd700', floatSpeed: 0.8, floatIntensity: 0.3,
  },
  offline: {
    bgTop: '#F5F5F5', bgBottom: '#E0E0E0',
    label: '\u79BB\u7EBF', intensity: 0.3, dotColor: '#B2BEC3',
    glowColor: '#888888', floatSpeed: 0.5, floatIntensity: 0.15,
  },
  error: {
    bgTop: '#FFF0F0', bgBottom: '#FFD5D5',
    label: '\u5F02\u5E38', intensity: 1.2, dotColor: '#E17055',
    glowColor: '#ff4444', floatSpeed: 1, floatIntensity: 0.3,
  },
};

/* --- GLB Model --- */
function GLBModel({ status }: { status: string }) {
  const groupRef = useRef<THREE.Group>(null!);
  const { scene } = useGLTF(MODEL_PATH);
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.online;

  const clonedScene = useMemo(() => {
    const clone = scene.clone(true);
    const box = new THREE.Box3().setFromObject(clone);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = 2 / maxDim;
    clone.scale.setScalar(scale);
    box.setFromObject(clone);
    const center = box.getCenter(new THREE.Vector3());
    clone.position.sub(center);
    return clone;
  }, [scene]);

  useFrame(({ clock }) => {
    if (!groupRef.current) return;
    const t = clock.getElapsedTime();
    const g = groupRef.current;

    // Breathing
    const breathSpeed = status === 'working' ? 3 : status === 'thinking' ? 1.5 : 2;
    const breathAmp = status === 'offline' ? 0.005 : 0.02;
    const breath = 1 + Math.sin(t * breathSpeed) * breathAmp;
    g.scale.set(breath, breath * (1 + Math.sin(t * breathSpeed) * breathAmp * 0.5), breath);

    // Position
    if (status === 'error') {
      g.position.x = Math.sin(t * 20) * 0.05;
      g.position.y = 0;
    } else if (status === 'working' || status === 'busy') {
      g.position.y = Math.abs(Math.sin(t * 3)) * 0.08;
      g.position.x = Math.sin(t * 2) * 0.03;
    } else if (status === 'thinking') {
      g.position.x = Math.sin(t * 0.8) * 0.05;
      g.position.y = Math.sin(t * 1.2) * 0.03;
    } else if (status === 'idle') {
      g.position.y = Math.sin(t * 0.5) * 0.02;
      g.position.x = 0;
    } else if (status === 'offline') {
      g.position.y = -0.05;
      g.position.x = 0;
    } else {
      g.position.y = Math.sin(t * 1.5) * 0.04;
      g.position.x = 0;
    }

    // Rotation
    if (status === 'working' || status === 'busy') {
      g.rotation.y = Math.sin(t * 1.8) * 0.2;
      g.rotation.z = Math.sin(t * 2.5) * 0.05;
    } else if (status === 'thinking') {
      g.rotation.y = Math.sin(t * 0.4) * 0.3;
      g.rotation.x = Math.sin(t * 0.6) * 0.05;
      g.rotation.z = 0;
    } else if (status === 'error') {
      g.rotation.y = 0;
      g.rotation.z = Math.sin(t * 15) * 0.03;
    } else if (status === 'idle') {
      g.rotation.y = Math.sin(t * 0.3) * 0.15;
      g.rotation.x = 0.05;
      g.rotation.z = Math.sin(t * 0.4) * 0.02;
    } else if (status === 'offline') {
      g.rotation.y = 0;
      g.rotation.x = 0.1;
      g.rotation.z = 0;
    } else {
      g.rotation.y = Math.sin(t * 0.7) * 0.1;
      g.rotation.z = Math.sin(t * 0.9) * 0.02;
      g.rotation.x = 0;
    }
  });

  return (
    <Float
      speed={config.floatSpeed}
      rotationIntensity={0.2}
      floatIntensity={config.floatIntensity}
    >
      <group ref={groupRef}>
        <primitive object={clonedScene} />
        {/* Ground glow */}
        <mesh position={[0, -1.2, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.5, 1, 32]} />
          <meshBasicMaterial
            color={config.glowColor}
            transparent
            opacity={0.15 * config.intensity}
            side={THREE.DoubleSide}
          />
        </mesh>
      </group>
    </Float>
  );
}

/* --- Loading Spinner --- */
function LoadingSpinner() {
  return (
    <mesh>
      <sphereGeometry args={[0.3, 16, 16]} />
      <meshBasicMaterial color="#FF9F43" wireframe transparent opacity={0.5} />
    </mesh>
  );
}

/* --- Particles --- */
function Particles({ count = 50, color, status }: { count?: number; color: string; status: string }) {
  const meshRef = useRef<THREE.InstancedMesh>(null!);

  const particles = useMemo(() => {
    const temp = [];
    for (let i = 0; i < count; i++) {
      temp.push({
        position: new THREE.Vector3(
          (Math.random() - 0.5) * 6,
          (Math.random() - 0.5) * 5,
          (Math.random() - 0.5) * 4,
        ),
        speed: 0.2 + Math.random() * 0.5,
        offset: Math.random() * Math.PI * 2,
      });
    }
    return temp;
  }, [count]);

  const dummy = useMemo(() => new THREE.Object3D(), []);

  useFrame(({ clock }) => {
    if (!meshRef.current) return;
    const t = clock.getElapsedTime();

    particles.forEach((p, i) => {
      dummy.position.set(
        p.position.x + Math.sin(t * p.speed + p.offset) * 0.3,
        p.position.y + Math.cos(t * p.speed * 0.7 + p.offset) * 0.3,
        p.position.z,
      );
      const s = status === 'offline' ? 0.3 : 0.5 + Math.sin(t * 2 + p.offset) * 0.3;
      dummy.scale.setScalar(s);
      dummy.updateMatrix();
      meshRef.current.setMatrixAt(i, dummy.matrix);
    });
    meshRef.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, count]}>
      <sphereGeometry args={[0.02, 6, 6]} />
      <meshBasicMaterial color={color} transparent opacity={0.4} />
    </instancedMesh>
  );
}

/* --- Scene --- */
function Scene({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.online;

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[3, 5, 4]} intensity={1} color="#fff5e6" />
      <directionalLight position={[-2, 3, -2]} intensity={0.3} color="#8888ff" />
      <pointLight position={[0, 2, 3]} intensity={0.4} color={config.glowColor} />

      <Suspense fallback={<LoadingSpinner />}>
        <GLBModel status={status} />
      </Suspense>

      <Particles
        count={status === 'offline' ? 15 : status === 'working' ? 80 : 40}
        color={config.glowColor}
        status={status}
      />

      <OrbitControls
        enableZoom={false}
        enablePan={false}
        minPolarAngle={Math.PI / 4}
        maxPolarAngle={Math.PI / 2}
        rotateSpeed={0.5}
      />
    </>
  );
}

/* --- CSS Fallback --- */
function CSSFallback({ status }: { status: string }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.online;
  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexDirection: 'column', gap: '16px',
    }}>
      <div style={{
        fontSize: '80px',
        filter: status === 'offline' ? 'grayscale(0.8) brightness(0.5)' : 'none',
        animation: status === 'working' ? 'claw-css-bounce 0.6s ease-in-out infinite' : 'claw-css-float 3s ease-in-out infinite',
      }}>
        {'\uD83E\uDD9E'}
      </div>
      <style>{`
        @keyframes claw-css-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes claw-css-bounce {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-8px) scale(1.05); }
        }
      `}</style>
      <span style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
        WebGL not available - {config.label}
      </span>
    </div>
  );
}

/* --- Status Badge --- */
function StatusBadge({ config }: { config: typeof STATUS_CONFIG[string] }) {
  return (
    <div style={{
      position: 'absolute', bottom: '12px', left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex', alignItems: 'center', gap: '6px',
      padding: '4px 14px', borderRadius: '20px',
      background: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(12px)',
      border: '1px solid rgba(0, 0, 0, 0.06)',
      zIndex: 10,
    }}>
      <div style={{
        width: '7px', height: '7px', borderRadius: '50%',
        background: config.dotColor,
        boxShadow: `0 0 6px ${config.dotColor}`,
      }} />
      <span style={{
        fontSize: '12px', color: 'var(--color-text)',
        fontWeight: 500, letterSpacing: '0.5px',
      }}>
        {config.label}
      </span>
    </div>
  );
}

/* --- Main Export --- */
export function Claw3DView({ status = 'online' }: { status?: string }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.online;
  const webglOk = useMemo(() => isWebGLAvailable(), []);
  const fallback = <CSSFallback status={status} />;

  return (
    <div style={{
      width: '100%',
      height: '200px',
      position: 'relative',
      overflow: 'hidden',
      background: `linear-gradient(180deg, ${config.bgTop} 0%, ${config.bgBottom} 100%)`,
      transition: 'background 1s ease',
    }}>
      {webglOk ? (
        <WebGLErrorBoundary fallback={fallback}>
          <Canvas
            camera={{ position: [0, 0.5, 3.5], fov: 45 }}
            dpr={[1, 2]}
            gl={{ antialias: true, alpha: false, failIfMajorPerformanceCaveat: false }}
            onCreated={({ gl }) => {
              gl.toneMapping = THREE.ACESFilmicToneMapping;
              gl.toneMappingExposure = 1.2;
            }}
            style={{ width: '100%', height: '100%' }}
          >
            <Scene status={status} />
          </Canvas>
        </WebGLErrorBoundary>
      ) : (
        fallback
      )}

      <StatusBadge config={config} />
    </div>
  );
}

// Preload model
useGLTF.preload(MODEL_PATH);
